import { IBaseAction } from '@models/actions/IBaseAction';
import { IListElement } from '@srm/ui/types/IListElement';
import { NoActionsItem } from '@models/actions/ActionConstants';
import { IConfig } from '@/configuration/models/IConfig';
import ActionKeyButton from '@atoms/ActionKeyButton/ActionKeyButton';
import React from 'react';
import { DEFAULT_ACTION_ICON, resolveMenuIcon } from '@molecules/Folders/CreateDocumentMenu/menuIcons';
import ActionParticipantMenuItem from '@molecules/ActionParticipant/ActionParticipantMenuItem';
import { getActionPerformer } from './actionPresentation';

const getActionMenuIconName = (action: IBaseAction): string | undefined => {
    const icon = action.options?.menu?.icon;
    return typeof icon === 'string' && icon.trim() ? icon.trim() : undefined;
};

const renderActionIcon = (action: IBaseAction, reserveIconSpace: boolean): React.ReactNode => {
    if (!reserveIconSpace) return undefined;

    const iconName = getActionMenuIconName(action);
    const Icon = iconName ? resolveMenuIcon(iconName, DEFAULT_ACTION_ICON) : undefined;

    return (
        <span
            data-action-icon={iconName}
            data-testid={iconName ? `action-menu-icon-${iconName}` : 'action-menu-icon-empty'}
        >
            {Icon && <Icon size={16} />}
        </span>
    );
};

interface IConvertActionsOptions {
    showMenuIcons?: boolean;
}

export const convertActions = (
    baseActs: IBaseAction[] | undefined,
    onActionClick: (action: IBaseAction) => void,
    showActionKeys: boolean,
    config: IConfig,
    renderEndAdornment?: (action: IBaseAction) => React.ReactNode,
    options: IConvertActionsOptions = {},
) => {
    if (baseActs === undefined || baseActs === null) return undefined;
    const reserveIconSpace = Boolean(options.showMenuIcons && baseActs.some((action) => getActionMenuIconName(action)));
    let acts = baseActs.map<IListElement>((x, i) => ({
        value: i.toString(),
        label: getActionPerformer(x) ? <ActionParticipantMenuItem action={x} onRun={onActionClick} /> : x.displayName,
        preventClick: Boolean(getActionPerformer(x)),
        startAdornment: renderActionIcon(x, reserveIconSpace),
        handler: () => {
            setTimeout(() => onActionClick(x), 1);
        },
        endAdornment: () => {
            const diagnosticAdornment = renderEndAdornment?.(x);
            const keyAdornment =
                config.environment !== 'PROD' && showActionKeys ? (
                    <div className="actions-menu--key">
                        <ActionKeyButton type={x.type} actionKey={x.key} position="right" />
                    </div>
                ) : null;

            if (!keyAdornment && !diagnosticAdornment) return null;
            return (
                <>
                    {keyAdornment}
                    {diagnosticAdornment}
                </>
            );
        },
    }));

    if (acts == null || acts.length === 0) {
        acts = [NoActionsItem];
    }

    return acts;
};
